import React, { useEffect, useState } from 'react';
import { enrollOfflineVault, getVaultStatus, isOfflineVaultUnlocked, recoverOfflineVault, unlockOfflineVault, resumeOfflineVaultSession } from '../offline/vault';
import { isOfflineChartRoute } from '../offline/offlineSession';
import { getOfflineOwnerIdentity } from '../offline/offlineIdentity';
import { migrateOwnedLegacyOfflineRecords } from '../offline/legacyVaultMigration';

export function OfflineVaultPanel({ enrolled = false, onComplete = () => {} }) {
  const [mode, setMode] = useState(enrolled ? 'unlock' : 'enroll');
  const [passphrase, setPassphrase] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async event => {
    event.preventDefault();
    if (mode !== 'unlock' && passphrase !== confirmation) { setError('The passphrases do not match.'); return; }
    setBusy(true); setError('');
    try {
      if (mode === 'enroll') await enrollOfflineVault(passphrase);
      else if (mode === 'recover') await recoverOfflineVault(passphrase);
      else await unlockOfflineVault(passphrase);
      await migrateOwnedLegacyOfflineRecords();
      setPassphrase(''); setConfirmation(''); onComplete();
    } catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  };
  return <form onSubmit={submit} className="space-y-3">
    <h2 className="text-lg font-semibold">{mode === 'unlock' ? 'Unlock device charting' : mode === 'recover' ? 'Recover device charting' : 'Set up a device passphrase'}</h2>
    <p className="text-sm text-gray-600">{mode === 'unlock'
      ? 'Enter this account’s device passphrase. Online reloads in this tab stay unlocked for up to eight hours while the same sign-in is valid. Offline reloads and a new sign-in require your passphrase. An open labor stays unlocked through sleep and session expiry.'
      : 'Use at least 12 characters. You can recover access after signing in again online. Existing unsynced records are kept.'}</p>
    <label className="block text-sm">Device passphrase<input autoComplete={mode === 'unlock' ? 'current-password' : 'new-password'} type="password" required minLength={mode === 'unlock' ? 1 : 12}
      value={passphrase} onChange={event => setPassphrase(event.target.value)} className="mt-1 block w-full rounded border p-2" /></label>
    {mode !== 'unlock' && <label className="block text-sm">Confirm device passphrase<input autoComplete="new-password" type="password" required value={confirmation}
      onChange={event => setConfirmation(event.target.value)} className="mt-1 block w-full rounded border p-2" /></label>}
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    <button disabled={busy} type="submit" className="rounded bg-[#4f7a64] px-4 py-2 text-white disabled:opacity-50">{busy ? 'Please wait…' : mode === 'unlock' ? 'Unlock' : mode === 'recover' ? 'Recover and set passphrase' : 'Set up device charting'}</button>
    {enrolled && <button type="button" onClick={() => { setMode(mode === 'recover' ? 'unlock' : 'recover'); setError(''); setPassphrase(''); setConfirmation(''); }} className="ml-2 text-sm underline">{mode === 'recover' ? 'Use existing passphrase' : 'Forgot passphrase'}</button>}
    {mode === 'recover' && <button type="button" onClick={() => window.open(`/login?from=${encodeURIComponent(window.location.pathname)}`, '_blank', 'noopener,noreferrer')} className="block text-sm underline">Sign in again for recovery</button>}
    <p className="text-xs text-gray-600">Older records are migrated only for this account and practice. Records with another or unknown owner are retained for recovery and may still use the older device protection.</p>
  </form>;
}

export default function OfflineVaultGate({ children, required = false }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const chartRoute = required || isOfflineChartRoute();
  const ownerKey = getOfflineOwnerIdentity()?.ownerKey || null;
  useEffect(() => {
    if (!chartRoute) return undefined;
    let current = true;
    const refresh = () => {
      const requestedOwner = getOfflineOwnerIdentity()?.ownerKey || null;
      setStatus(previous => previous?.ownerKey !== requestedOwner ? null
        : previous?.enrolled && !isOfflineVaultUnlocked() ? { ...previous, unlocked: false } : previous);
      return getVaultStatus().then(value => {
        if (current && requestedOwner === (getOfflineOwnerIdentity()?.ownerKey || null)) { setStatus({ ...value, ownerKey: requestedOwner }); setError(''); }
      }).catch(() => { if (current) setError('Device storage could not be opened. Reopen this tab to retry; your saved device records have not been removed.'); });
    };
    const resume = () => Promise.resolve(typeof resumeOfflineVaultSession === 'function' && resumeOfflineVaultSession()).catch(() => false).then(refresh);
    resume();
    window.addEventListener('online', resume);
    window.addEventListener('vela:offline-vault-changed', refresh);
    window.addEventListener('vela:logged-in', refresh);
    window.addEventListener('storage', refresh);
    return () => { current = false; window.removeEventListener('online', resume); window.removeEventListener('vela:offline-vault-changed', refresh); window.removeEventListener('vela:logged-in', refresh); window.removeEventListener('storage', refresh); };
  }, [chartRoute, ownerKey]);
  if (!chartRoute) return children;
  if (error) return <div role="alert" className="mx-auto max-w-lg p-8">{error}</div>;
  if (!status || status.ownerKey !== ownerKey) return <div role="status" className="p-8">Opening device charting…</div>;
  if (status.enrolled && !status.unlocked) return <div className="mx-auto max-w-lg p-8"><OfflineVaultPanel enrolled /></div>;
  return children;
}
